import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView, useScroll, useTransform } from "framer-motion";

/* ──────────────────────────────────────────────
   IMAGES from the OH YAAS! PDF
────────────────────────────────────────────── */
const IMG = {
  heroSigning: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_1_1781628680047327292.jpg",
  haulHeader: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_1_1781628680245843087.jpg",
  haul1: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_6_1781628680269187216.jpg",
  haul2: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_7_1781628680294122311.jpg",
  haul3: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_8_1781628680319100394.jpg",
  haul4: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_9_1781628680346751364.jpg",
  announcement: "https://static-us-img.skywork.ai/prod/nexus/1781628679/cropped_image_6_1781628679908182968.jpg",
  dayLife1: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_6_1781628680346657054.jpg",
  dayLife2: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_7_1781628680389985015.jpg",
  dayLife3: "https://static-us-img.skywork.ai/prod/nexus/1781628680/cropped_image_8_1781628680391890866.jpg",
  // Shipping — Sticker Signing
  shipSign1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_4_1781631201575078843.jpg",
  shipSignPens: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_8_1781631201575888526.jpg",
  shipSignEx1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_10_1781631201577450813.jpg",
  shipSignEx2: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_13_1781631201581181643.jpg",
  shipSignEx3: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_14_1781631201581950592.jpg",
  shipSignSheet: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_28_1781631201595562275.jpg",
  shipSignUpload: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_30_1781631201596650522.jpg",
  shipSignAttest: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_31_1781631201598076663.jpg",
  shipSignUPS: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_34_1781631201599119083.jpg",
  // Shipping — Autograph packaging
  shipAuto1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_3_1781631201451525183.jpg",
  shipAuto2: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_5_1781631201453929742.jpg",
  shipAuto3: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_7_1781631201454585611.jpg",
  shipAutoAttest: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_14_1781631201456684910.jpg",
  shipAutoUPS1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_11_1781631201455125153.jpg",
  shipAutoUPS2: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_12_1781631201455732546.jpg",
  // Shipping — Relic
  shipRelic1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_1_1781631201708594693.jpg",
  shipRelicLabel: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_3_1781631201709172985.jpg",
  shipRelicBag: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_5_1781631201709815938.jpg",
  shipRelicPadded: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_7_1781631201710348522.jpg",
  shipRelicUPS: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_9_1781631201710869374.jpg",
  shipRelicPack1: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_23_1781631201726728681.jpg",
  shipRelicPack2: "https://static-us-img.skywork.ai/prod/nexus/1781631201/cropped_image_28_1781631201733187551.jpg",
};

const NAV_SECTIONS = [
  { id: "hero", label: "Welcome" },
  { id: "commercial", label: "BKFC Commercial" },
  { id: "signing", label: "The Signing" },
  { id: "haul", label: "Haul & Live" },
  { id: "announcement", label: "Announcement" },
  { id: "daylife", label: "Day in the Life" },
  { id: "shipping", label: "Shipping" },
  { id: "collecting", label: "Collecting Matters" },
  { id: "thanks", label: "Thank You" },
];

const GOLD = "#D4A017";
const GOLD_LIGHT = "#F5D060";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (d: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const, delay: d },
  }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11 } },
};

function AnimSection({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-10%" });
  return (
    <motion.div
      ref={ref}
      variants={staggerContainer}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      className={className}
    >
      {children}
    </motion.div>
  );
}

function GoldDivider() {
  return (
    <div className="flex items-center gap-3 my-2">
      <div style={{ height: 2, flex: 1, background: `linear-gradient(to right, transparent, ${GOLD})` }} />
      <div style={{ width: 8, height: 8, background: GOLD, transform: "rotate(45deg)" }} />
      <div style={{ height: 2, flex: 1, background: `linear-gradient(to left, transparent, ${GOLD})` }} />
    </div>
  );
}

function StepCard({ num, text }: { num: number; text: string }) {
  return (
    <motion.div
      variants={fadeUp}
      className="flex gap-4 items-start p-5 rounded-xl cursor-default"
      style={{ background: "rgba(255,255,255,0.04)", border: `1px solid rgba(212,160,23,0.2)` }}
      whileHover={{ borderColor: `rgba(212,160,23,0.6)`, background: "rgba(212,160,23,0.07)", transition: { duration: 0.2 } }}
    >
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
        style={{ border: `2px solid ${GOLD}`, color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}
      >
        {num}
      </div>
      <p className="text-gray-300 leading-relaxed text-sm uppercase tracking-wide mt-1">{text}</p>
    </motion.div>
  );
}

function PlatformTag({ label }: { label: string }) {
  return (
    <span
      className="inline-block text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-widest"
      style={{ background: "rgba(212,160,23,0.15)", color: GOLD, border: "1px solid rgba(212,160,23,0.35)" }}
    >
      {label}
    </span>
  );
}

function ImgCard({ src, delay = 0 }: { src: string; delay?: number }) {
  return (
    <motion.div
      variants={fadeUp}
      custom={delay}
      className="relative overflow-hidden rounded-xl"
      style={{ aspectRatio: "4/3", border: "1px solid rgba(212,160,23,0.15)" }}
      whileHover={{ scale: 1.04, transition: { duration: 0.3 } }}
    >
      <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }} />
      <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />
    </motion.div>
  );
}

export default function Index() {
  const [active, setActive] = useState("hero");
  const [navOpen, setNavOpen] = useState(false);
  const [shippingTab, setShippingTab] = useState(0);
  const [activeHaulStep, setActiveHaulStep] = useState(0);
  const [haulZoom, setHaulZoom] = useState(false);
  const [activeAnnounceStep, setActiveAnnounceStep] = useState(0);
  const [announceZoom, setAnnounceZoom] = useState(false);
  const [dayZoom, setDayZoom] = useState(-1);
  const [shipSignVidIdx, setShipSignVidIdx] = useState(0);
  const [collectEmail, setCollectEmail] = useState("");
  const [collectComment, setCollectComment] = useState("");
  const [raMuted, setRaMuted] = useState(true);
  const raVideoRef = useRef<HTMLVideoElement>(null);
  const bgAudioRef = useRef<HTMLAudioElement>(null);
  const [bgMuted, setBgMuted] = useState(false);
  const [bgVolume, setBgVolume] = useState(0.5);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);
  const welcomeRef = useRef<HTMLHeadingElement>(null);
  const [musicStarted, setMusicStarted] = useState(false);
  const { scrollYProgress } = useScroll();
  const progressWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { threshold: 0.3 }
    );
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  // Music + beat pulse + YouTube pause detection
  // NOTE: No AudioContext/createMediaElementSource — that silently mutes audio on Safari iOS.
  // Audio plays 100% natively. Welcome text pulses via setInterval at song BPM.
  useEffect(() => {
    const audio = bgAudioRef.current;
    if (!audio) return;
    audio.volume = 0.5;

    let pulseInterval: ReturnType<typeof setInterval> | null = null;
    const startPulse = () => {
      if (pulseInterval) return;
      pulseInterval = setInterval(() => {
        if (welcomeRef.current) {
          welcomeRef.current.style.transform = "scale(1.045)";
          welcomeRef.current.style.textShadow = "0 0 22px rgba(212,160,23,0.55)";
          setTimeout(() => {
            if (welcomeRef.current) {
              welcomeRef.current.style.transform = "scale(1)";
              welcomeRef.current.style.textShadow = "none";
            }
          }, 130);
        }
      }, 480); // ~125 BPM
    };

    const startMusic = () => {
      audio.play().catch(() => {});
      setBgMuted(false);
      setMusicStarted(true);
      startPulse();
    };

    // Try autoplay on mount
    audio.play().then(() => {
      setBgMuted(false);
      setMusicStarted(true);
      startPulse();
    }).catch(() => {
      // Blocked (Safari/iOS) — fire on very first user interaction
      const handler = () => startMusic();
      ["click", "touchstart", "keydown"].forEach(ev =>
        document.addEventListener(ev, handler, { once: true })
      );
    });

    // Pause bg music when a YouTube video plays
    const pauseBg = () => {
      if (bgAudioRef.current) { bgAudioRef.current.pause(); setBgMuted(true); }
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.head.appendChild(tag);
    (window as any).onYouTubeIframeAPIReady = () => {
      ["yt-commercial", "yt-collecting"].forEach((id) => {
        new (window as any).YT.Player(id, {
          events: { onStateChange: (e: any) => { if (e.data === 1) pauseBg(); } },
        });
      });
    };

    return () => {
      tag.remove();
      if (pulseInterval) clearInterval(pulseInterval);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setNavOpen(false);
  };

  return (
    <div style={{ background: "#000", color: "#fff", fontFamily: "'Inter', sans-serif", overflowX: "hidden" }}>

      {/* Background music */}
      <audio ref={bgAudioRef} src="/yaas-bg.mp3" loop preload="auto" playsInline />

      {/* Persistent Music Control Panel — bottom left */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        className="fixed bottom-6 left-6 z-50 flex flex-col gap-2 p-3 rounded-2xl"
        style={{
          background: "rgba(0,0,0,0.85)",
          border: `1px solid ${bgMuted ? "rgba(255,255,255,0.12)" : "rgba(212,160,23,0.45)"}`,
          backdropFilter: "blur(14px)",
          minWidth: 170,
        }}
      >
        {/* Tap to play — shown on Safari/mobile before first interaction */}
        {!musicStarted && (
          <button
            onClick={() => {
              const audio = bgAudioRef.current;
              if (!audio) return;
              audio.volume = bgVolume;
              audio.play().then(() => { setBgMuted(false); setMusicStarted(true); }).catch(() => {});
            }}
            className="w-full text-xs uppercase tracking-widest py-1.5 rounded-lg animate-pulse"
            style={{ color: "#000", fontFamily: "'JetBrains Mono', monospace", background: `linear-gradient(135deg, ${GOLD}, #F5D060)`, fontWeight: 700 }}
          >
            🎵 Tap to Play
          </button>
        )}
        {/* Label */}
        <div className="flex items-center justify-between">
          <span style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} className="uppercase tracking-widest">
            {bgMuted ? "🔇" : "🎵"} YAAS FM
          </span>
          <span style={{ color: "#555", fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }}>
            {bgMuted ? "OFF" : `${Math.round(bgVolume * 100)}%`}
          </span>
        </div>
        {/* Volume slider */}
        <input
          type="range" min={0} max={100}
          value={bgMuted ? 0 : Math.round(bgVolume * 100)}
          onChange={e => {
            const v = Number(e.target.value) / 100;
            setBgVolume(v);
            if (bgAudioRef.current) bgAudioRef.current.volume = v;
            if (v > 0 && bgMuted) { setBgMuted(false); bgAudioRef.current?.play().catch(() => {}); }
            if (v === 0) setBgMuted(true);
          }}
          className="w-full h-1 rounded-full appearance-none cursor-pointer"
          style={{ accentColor: GOLD, background: `linear-gradient(to right, ${GOLD} ${bgMuted ? 0 : Math.round(bgVolume * 100)}%, rgba(255,255,255,0.1) ${bgMuted ? 0 : Math.round(bgVolume * 100)}%)` }}
        />
        {/* Mute toggle */}
        <button
          onClick={() => {
            const audio = bgAudioRef.current;
            if (!audio) return;
            if (bgMuted) { audio.volume = bgVolume || 0.5; audio.play().catch(() => {}); setBgMuted(false); setMusicStarted(true); }
            else { audio.pause(); setBgMuted(true); }
          }}
          className="text-xs uppercase tracking-widest py-1.5 rounded-lg transition-all duration-200"
          style={{
            color: bgMuted ? "#555" : GOLD,
            fontFamily: "'JetBrains Mono', monospace",
            background: bgMuted ? "rgba(255,255,255,0.04)" : "rgba(212,160,23,0.1)",
            border: `1px solid ${bgMuted ? "rgba(255,255,255,0.08)" : "rgba(212,160,23,0.3)"}`,
          }}
        >
          {bgMuted ? "🔇 Unmute" : "🔊 Mute"}
        </button>
      </motion.div>

      {/* ── PROGRESS BAR ── */}
      <motion.div
        className="fixed top-0 left-0 z-50 h-0.5"
        style={{ width: progressWidth, background: `linear-gradient(to right, ${GOLD}, ${GOLD_LIGHT})` }}
      />

      {/* ── STICKY NAV ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-6 py-4"
        style={{ background: "rgba(0,0,0,0.88)", borderBottom: "1px solid rgba(212,160,23,0.15)" }}
      >
        <div className="flex items-center gap-2 sm:gap-3">
          <img src="/ohyaas.svg" alt="OH YAAS!" className="h-7 sm:h-9" />
          <img src="/bkfc.png" alt="Bare Knuckle Fighting Championship" className="h-[42px] sm:h-[59px]" style={{ opacity: 0.85 }} />
        </div>

        <ul className="hidden md:flex gap-7 items-center">
          {NAV_SECTIONS.map(({ id, label }) => (
            <li key={id} className="relative">
              <button
                onClick={() => scrollTo(id)}
                className="text-xs uppercase tracking-widest transition-colors duration-200"
                style={{ color: active === id ? GOLD : "#777", fontWeight: active === id ? 600 : 400 }}
              >
                {label}
              </button>
              {active === id && (
                <motion.div layoutId="underline" className="absolute -bottom-1 left-0 right-0 h-px" style={{ background: GOLD }} />
              )}
            </li>
          ))}
        </ul>

        <button
          className="md:hidden p-1"
          onClick={() => setNavOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          <div className="flex flex-col gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="block w-6 h-px transition-all duration-300 origin-center"
                style={{
                  background: GOLD,
                  opacity: navOpen && i === 1 ? 0 : 1,
                  transform: navOpen
                    ? i === 0 ? "translateY(8px) rotate(45deg)"
                    : i === 2 ? "translateY(-8px) rotate(-45deg)"
                    : "none"
                    : "none",
                }}
              />
            ))}
          </div>
        </button>
      </nav>

      <AnimatePresence>
        {navOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="fixed top-16 left-0 right-0 z-30 md:hidden"
            style={{ background: "rgba(0,0,0,0.97)", borderBottom: "1px solid rgba(212,160,23,0.2)" }}
          >
            {NAV_SECTIONS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className="w-full text-left px-6 py-4 text-sm uppercase tracking-widest transition-colors"
                style={{ color: active === id ? GOLD : "#aaa", borderBottom: "1px solid rgba(212,160,23,0.08)" }}
              >
                {label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ════════════════════════════════════
          SECTION 1 — HERO
      ════════════════════════════════════ */}
      {/* @section: hero */}
      <section
        id="hero"
        className="relative flex flex-col items-center justify-center min-h-screen text-center px-6"
        style={{ paddingTop: 80 }}
      >
        <div className="absolute inset-0 z-0">
          <img src={IMG.heroSigning} alt="" className="w-full h-full object-cover" style={{ opacity: 0.22 }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.8) 65%, #000 100%)" }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Logos above Experiential Collectibles */}
          <motion.div
            variants={fadeUp} initial="hidden" animate="visible" custom={0}
            className="flex items-center justify-center gap-4 mb-6"
          >
            <img src="/ohyaas.svg" alt="OH YAAS!" style={{ height: 44 }} />
            <img src="/bkfc.png" alt="Bare Knuckle Fighting Championship" style={{ height: 68, opacity: 0.85 }} />
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={0.08}
            className="text-xs uppercase tracking-[0.45em] mb-6"
            style={{ color: GOLD }}
          >
            Experiential Collectibles™ Partner Program
          </motion.p>

          <motion.h1
            ref={welcomeRef}
            variants={fadeUp} initial="hidden" animate="visible" custom={0.1}
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(64px, 14vw, 128px)",
              lineHeight: 0.9,
              letterSpacing: 4,
              background: `linear-gradient(135deg, #fff 0%, ${GOLD} 55%, ${GOLD_LIGHT} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              willChange: "transform, text-shadow",
              transformOrigin: "center center",
            }}
          >
            WELCOME TO<br />OH YAAS!
          </motion.h1>

          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0.25} className="mt-6 mb-8">
            <GoldDivider />
          </motion.div>

          <motion.p
            variants={fadeUp} initial="hidden" animate="visible" custom={0.35}
            className="text-gray-400 text-sm sm:text-base uppercase tracking-widest leading-relaxed max-w-xl mx-auto"
          >
            OH YAAS × BKFC Fighter Highlights — Content Creation Guide
          </motion.p>

          <motion.button
            variants={fadeUp} initial="hidden" animate="visible" custom={0.45}
            onClick={() => scrollTo("commercial")}
            className="mt-10 px-10 py-4 rounded-full text-sm uppercase tracking-widest font-semibold"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, color: "#000" }}
            whileHover={{ scale: 1.06, boxShadow: `0 0 35px rgba(212,160,23,0.55)` }}
            whileTap={{ scale: 0.97 }}
          >
            Let's Get Started ↓
          </motion.button>
        </div>

        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <span className="text-xs uppercase tracking-widest" style={{ color: GOLD, opacity: 0.6 }}>Scroll</span>
          <div className="w-px h-10" style={{ background: `linear-gradient(to bottom, ${GOLD}, transparent)` }} />
        </motion.div>
      </section>

      {/* ════════════════════════════════════
          SECTION — BKFC COMMERCIAL
      ════════════════════════════════════ */}
      {/* @section: commercial */}
      <section id="commercial" className="relative py-24 px-6" style={{ background: "linear-gradient(180deg, #000 0%, #080500 50%, #000 100%)" }}>
        <div className="max-w-5xl mx-auto">
          <AnimSection>
            {/* Header */}
            <div className="text-center mb-12">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                OH YAAS × BKFC
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", letterSpacing: 3, lineHeight: 1 }}
              >
                THE BKFC{" "}
                <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  COMMERCIAL
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto mt-4"><GoldDivider /></motion.div>
            </div>

            {/* Video embed */}
            <motion.div
              variants={fadeUp}
              className="relative rounded-2xl overflow-hidden"
              style={{
                border: "1px solid rgba(212,160,23,0.35)",
                boxShadow: "0 0 60px rgba(212,160,23,0.1)",
              }}
              whileHover={{ boxShadow: "0 0 80px rgba(212,160,23,0.2)", transition: { duration: 0.4 } }}
            >
              {/* Gold corner accents */}
              <div className="absolute top-0 left-0 w-14 h-14 z-10 pointer-events-none"
                style={{ borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`, borderTopLeftRadius: "1rem" }} />
              <div className="absolute top-0 right-0 w-14 h-14 z-10 pointer-events-none"
                style={{ borderTop: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`, borderTopRightRadius: "1rem" }} />
              <div className="absolute bottom-0 left-0 w-14 h-14 z-10 pointer-events-none"
                style={{ borderBottom: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`, borderBottomLeftRadius: "1rem" }} />
              <div className="absolute bottom-0 right-0 w-14 h-14 z-10 pointer-events-none"
                style={{ borderBottom: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`, borderBottomRightRadius: "1rem" }} />

              <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
                <iframe
                  id="yt-commercial"
                  src="https://www.youtube.com/embed/3AuEz4iZZvY?rel=0&modestbranding=1&color=white&enablejsapi=1"
                  title="BKFC Commercial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{
                    position: "absolute",
                    top: 0, left: 0,
                    width: "100%", height: "100%",
                    border: "none",
                  }}
                />
              </div>
            </motion.div>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 2 — THE SIGNING MOMENT
      ════════════════════════════════════ */}
      {/* @section: signing */}
      <section id="signing" className="relative min-h-screen flex items-center py-24 px-6">
        <div className="max-w-6xl mx-auto w-full">
          <AnimSection>
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                  Chapter 01
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", lineHeight: 1, letterSpacing: 3 }}
                >
                  THE SIGNING<br /><span style={{ color: GOLD }}>MOMENT</span>
                </motion.h2>

                <motion.div variants={fadeUp} className="my-6"><GoldDivider /></motion.div>

                <motion.p variants={fadeUp} className="text-gray-300 text-sm leading-relaxed uppercase tracking-wide mb-8">
                  Every fighter signing follows a consistent visual format. The moment a fighter puts on{" "}
                  <span style={{ color: GOLD, fontWeight: 600 }}>OH YAAS × BKFC gear</span> and engages with the logo
                  on camera is the signing moment.
                </motion.p>

                <motion.div
                  variants={fadeUp}
                  className="p-6 rounded-2xl"
                  style={{ background: "rgba(212,160,23,0.07)", border: "1px solid rgba(212,160,23,0.3)" }}
                >
                  <p className="text-sm uppercase tracking-widest mb-3 font-semibold" style={{ color: GOLD }}>
                    🏆 Why This Matters
                  </p>
                  <p className="text-gray-300 text-sm leading-relaxed uppercase tracking-wide">
                    Repetition across fighters builds brand recognition and creates a cultural shorthand — the way an{" "}
                    <span style={{ color: GOLD_LIGHT }}>NFL Draft pick putting on the team hat signals arrival.</span>
                  </p>
                </motion.div>
              </div>

              <motion.div
                variants={fadeUp}
                className="relative rounded-2xl overflow-hidden"
                style={{ aspectRatio: "3/4", border: "1px solid rgba(212,160,23,0.25)" }}
                whileHover={{ scale: 1.02, transition: { duration: 0.4 } }}
              >
                <img src="/signing-moment.webp" alt="Fighter opening OH YAAS signing kit" className="w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 60%)" }} />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-xs uppercase tracking-widest" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>
                    OH YAAS × BKFC — Signing Ritual
                  </p>
                </div>
                <div className="absolute top-0 left-0 w-12 h-12" style={{ borderTop: `2px solid ${GOLD}`, borderLeft: `2px solid ${GOLD}`, borderTopLeftRadius: "1rem" }} />
                <div className="absolute bottom-0 right-0 w-12 h-12" style={{ borderBottom: `2px solid ${GOLD}`, borderRight: `2px solid ${GOLD}`, borderBottomRightRadius: "1rem" }} />
              </motion.div>
            </div>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 3 — HAUL & LIVESTREAM
      ════════════════════════════════════ */}
      {/* @section: haul */}
      <section id="haul" className="relative py-24 px-6" style={{ background: "linear-gradient(180deg, #000 0%, #0d0900 50%, #000 100%)" }}>
        <div className="max-w-6xl mx-auto">
          <AnimSection>
            <div className="text-center mb-16">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                Chapter 02
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", letterSpacing: 3, lineHeight: 1 }}
              >
                HAUL &{" "}
                <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  LIVESTREAM
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto mt-4"><GoldDivider /></motion.div>
              <motion.p variants={fadeUp} className="text-gray-400 text-sm uppercase tracking-widest mt-4">
                Unpack Your Signing Kit Live or On Camera
              </motion.p>
            </div>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-3 justify-center mb-12">
              {["IG Live", "TikTok", "Short-Form Haul Video"].map((p) => (<PlatformTag key={p} label={p} />))}
            </motion.div>

            <motion.div
              variants={fadeUp}
              className="relative rounded-2xl overflow-hidden mb-14"
              style={{ height: "clamp(200px, 35vw, 420px)", border: "1px solid rgba(212,160,23,0.2)" }}
            >
              <img src="/haul-banner.webp" alt="Fighter unboxing OH YAAS kit" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,0,0,0.75) 0%, transparent 60%)" }} />
              <div className="absolute inset-y-0 left-0 flex items-center pl-10">
                <div>
                  <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>The Setup</p>
                  <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(28px, 4vw, 48px)", letterSpacing: 2, maxWidth: 300, color: "#fff" }}>
                    Unbox It Live
                  </p>
                </div>
              </div>
            </motion.div>

            {/* ── Interactive step + photo layout ── */}
            {(() => {
              const HAUL_STEPS = [
                { text: "Unpack your signing kit live or on camera — make it an event", img: "/haul-step1.webp" },
                { text: "Pull out the hat, shirt, stickers, and collectible assets one by one", img: "/haul-step2.webp" },
                { text: "Show the OH YAAS card pack included in the kit to the camera", img: "/haul-step3.webp" },
                { text: "Scan the QR code on camera to reveal the digital unlock", img: "/haul-step4.webp" },
                { text: "React to the collectible — pull up your fighter card and talk about it", img: "/haul-step5.webp" },
              ];
              const prev = () => setActiveHaulStep((s) => (s - 1 + HAUL_STEPS.length) % HAUL_STEPS.length);
              const next = () => setActiveHaulStep((s) => (s + 1) % HAUL_STEPS.length);
              return (
                <motion.div variants={fadeUp} className="grid lg:grid-cols-2 gap-8 items-start">
                  {/* Step list */}
                  <div className="flex flex-col gap-3">
                    {HAUL_STEPS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveHaulStep(i)}
                        className="flex gap-4 items-start p-5 rounded-xl text-left transition-all duration-300 w-full"
                        style={{
                          background: activeHaulStep === i ? "rgba(212,160,23,0.12)" : "rgba(255,255,255,0.03)",
                          border: activeHaulStep === i ? "1px solid rgba(212,160,23,0.6)" : "1px solid rgba(212,160,23,0.15)",
                        }}
                      >
                        <div
                          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                          style={{
                            border: `2px solid ${activeHaulStep === i ? GOLD : "rgba(212,160,23,0.35)"}`,
                            color: activeHaulStep === i ? "#000" : GOLD,
                            background: activeHaulStep === i ? GOLD : "transparent",
                            fontFamily: "'JetBrains Mono', monospace",
                          }}
                        >
                          {i + 1}
                        </div>
                        <p className="text-sm uppercase tracking-wide leading-relaxed mt-1"
                          style={{ color: activeHaulStep === i ? "#fff" : "#9ca3af" }}>
                          {s.text}
                        </p>
                      </button>
                    ))}
                  </div>

                  {/* Photo panel */}
                  <div className="sticky top-24">
                    {/* Main photo */}
                    <div className="relative rounded-2xl overflow-hidden mb-4"
                      style={{ border: "1px solid rgba(212,160,23,0.3)" }}>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeHaulStep}
                          initial={{ opacity: 0, scale: 1.04 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.97 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="relative cursor-zoom-in"
                          style={{ aspectRatio: "4/3" }}
                          onClick={() => setHaulZoom(true)}
                        >
                          <img
                            src={HAUL_STEPS[activeHaulStep].img}
                            alt={`Step ${activeHaulStep + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }} />
                          {/* Zoom hint */}
                          <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs uppercase tracking-widest"
                            style={{ background: "rgba(0,0,0,0.6)", color: GOLD, border: "1px solid rgba(212,160,23,0.3)" }}>
                            ⤢ Expand
                          </div>
                          {/* Step badge */}
                          <div className="absolute bottom-4 left-4">
                            <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full font-bold"
                              style={{ background: GOLD, color: "#000", fontFamily: "'JetBrains Mono', monospace" }}>
                              Step {activeHaulStep + 1} of {HAUL_STEPS.length}
                            </span>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Prev / Next controls */}
                    <div className="flex items-center justify-between gap-3">
                      <button
                        onClick={prev}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition-all duration-200"
                        style={{ background: "rgba(255,255,255,0.05)", color: "#888", border: "1px solid rgba(212,160,23,0.2)" }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.6)"; (e.currentTarget as HTMLButtonElement).style.color = GOLD; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.2)"; (e.currentTarget as HTMLButtonElement).style.color = "#888"; }}
                      >
                        ← Prev
                      </button>
                      {/* Dot indicators */}
                      <div className="flex gap-2">
                        {HAUL_STEPS.map((_, i) => (
                          <button key={i} onClick={() => setActiveHaulStep(i)}
                            className="rounded-full transition-all duration-300"
                            style={{ width: activeHaulStep === i ? 20 : 8, height: 8, background: activeHaulStep === i ? GOLD : "rgba(212,160,23,0.3)" }}
                          />
                        ))}
                      </div>
                      <button
                        onClick={next}
                        className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition-all duration-200"
                        style={{ background: "rgba(255,255,255,0.05)", color: "#888", border: "1px solid rgba(212,160,23,0.2)" }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.6)"; (e.currentTarget as HTMLButtonElement).style.color = GOLD; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.2)"; (e.currentTarget as HTMLButtonElement).style.color = "#888"; }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* ── Lightbox ── */}
            <AnimatePresence>
              {haulZoom && (
                <motion.div
                  key="haul-zoom"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-zoom-out"
                  style={{ background: "rgba(0,0,0,0.92)" }}
                  onClick={() => setHaulZoom(false)}
                >
                  <motion.div
                    initial={{ scale: 0.85, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.85, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ maxWidth: "90vw", maxHeight: "88vh", border: "1px solid rgba(212,160,23,0.4)" }}
                    onClick={e => e.stopPropagation()}
                  >
                    {(() => {
                      const HAUL_STEPS = [
                        { text: "", img: "/haul-step1.webp" },
                        { text: "", img: "/haul-step2.webp" },
                        { text: "", img: "/haul-step3.webp" },
                        { text: "", img: "/haul-step4.webp" },
                        { text: "", img: "/haul-step5.webp" },
                      ];
                      return (
                        <img
                          src={HAUL_STEPS[activeHaulStep].img}
                          alt={`Step ${activeHaulStep + 1}`}
                          style={{ maxWidth: "85vw", maxHeight: "80vh", objectFit: "contain", display: "block" }}
                        />
                      );
                    })()}
                    <button
                      onClick={() => setHaulZoom(false)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all"
                      style={{ background: "rgba(0,0,0,0.7)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)" }}
                    >
                      ✕
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 px-5 py-3 text-center"
                      style={{ background: "rgba(0,0,0,0.7)" }}>
                      <span className="text-xs uppercase tracking-widest" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>
                        Step {activeHaulStep + 1} of 5
                      </span>
                    </div>
                  </motion.div>
                  <p className="absolute bottom-6 text-xs uppercase tracking-widest text-gray-500">Click anywhere to close</p>
                </motion.div>
              )}
            </AnimatePresence>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 4 — ANNOUNCEMENT SERIES
      ════════════════════════════════════ */}
      {/* @section: announcement */}
      <section id="announcement" className="relative py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <AnimSection>
            {/* Header */}
            <div className="text-center mb-14">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                Chapter 03
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", lineHeight: 1, letterSpacing: 3 }}
              >
                ANNOUNCEMENT<br /><span style={{ color: GOLD }}>SERIES</span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto my-4"><GoldDivider /></motion.div>
              <motion.p variants={fadeUp} className="text-gray-400 text-xs uppercase tracking-widest mb-2">
                Content announcing each new signing
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 justify-center mt-4">
                {["IG Live", "TikTok", "Short-Form Haul Video"].map((p) => (<PlatformTag key={p} label={p} />))}
              </motion.div>
            </div>

            {/* Interactive step + photo layout */}
            {(() => {
              const ANNOUNCE_STEPS = [
                { text: "Put on the OH YAAS hat, tee shirt or show the cards to the camera", img: "/announce-step1.webp" },
                { text: "Show the OH YAAS card to the camera — flip it back and forth", img: "/announce-step2.webp" },
                { text: "Put on the OH YAAS clothes, explain why you're excited to have your own card", img: "/announce-step3.webp" },
                { text: "Show the card signing or relic — pull up the fighter card and talk about it", img: "/announce-step4.webp" },
              ];
              const prev = () => setActiveAnnounceStep((s) => (s - 1 + ANNOUNCE_STEPS.length) % ANNOUNCE_STEPS.length);
              const next = () => setActiveAnnounceStep((s) => (s + 1) % ANNOUNCE_STEPS.length);
              return (
                <motion.div variants={fadeUp} className="grid lg:grid-cols-2 gap-8 items-start">
                  {/* Step list */}
                  <div className="flex flex-col gap-3">
                    {ANNOUNCE_STEPS.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveAnnounceStep(i)}
                        className="flex gap-4 items-start p-5 rounded-xl text-left transition-all duration-300 w-full"
                        style={{
                          background: activeAnnounceStep === i ? "rgba(212,160,23,0.12)" : "rgba(255,255,255,0.03)",
                          border: activeAnnounceStep === i ? "1px solid rgba(212,160,23,0.6)" : "1px solid rgba(212,160,23,0.15)",
                        }}
                      >
                        <div
                          className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                          style={{
                            border: `2px solid ${activeAnnounceStep === i ? GOLD : "rgba(212,160,23,0.35)"}`,
                            color: activeAnnounceStep === i ? "#000" : GOLD,
                            background: activeAnnounceStep === i ? GOLD : "transparent",
                            fontFamily: "'JetBrains Mono', monospace",
                          }}
                        >
                          {i + 1}
                        </div>
                        <p className="text-sm uppercase tracking-wide leading-relaxed mt-1"
                          style={{ color: activeAnnounceStep === i ? "#fff" : "#9ca3af" }}>
                          {s.text}
                        </p>
                      </button>
                    ))}
                  </div>

                  {/* Photo panel */}
                  <div className="sticky top-24">
                    <div className="relative rounded-2xl overflow-hidden mb-4"
                      style={{ border: "1px solid rgba(212,160,23,0.3)" }}>
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={activeAnnounceStep}
                          initial={{ opacity: 0, scale: 1.04 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.97 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="relative cursor-zoom-in"
                          style={{ aspectRatio: "4/3" }}
                          onClick={() => setAnnounceZoom(true)}
                        >
                          <img
                            src={ANNOUNCE_STEPS[activeAnnounceStep].img}
                            alt={`Step ${activeAnnounceStep + 1}`}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 60%)" }} />
                          <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs uppercase tracking-widest"
                            style={{ background: "rgba(0,0,0,0.6)", color: GOLD, border: "1px solid rgba(212,160,23,0.3)" }}>
                            ⤢ Expand
                          </div>
                          <div className="absolute bottom-4 left-4">
                            <span className="text-xs uppercase tracking-widest px-3 py-1 rounded-full font-bold"
                              style={{ background: GOLD, color: "#000", fontFamily: "'JetBrains Mono', monospace" }}>
                              Step {activeAnnounceStep + 1} of {ANNOUNCE_STEPS.length}
                            </span>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>

                    {/* Prev / Next */}
                    <div className="flex items-center justify-between gap-3">
                      <button onClick={prev}
                        className="flex-1 flex items-center justify-center py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition-all duration-200"
                        style={{ background: "rgba(255,255,255,0.05)", color: "#888", border: "1px solid rgba(212,160,23,0.2)" }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.6)"; (e.currentTarget as HTMLButtonElement).style.color = GOLD; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.2)"; (e.currentTarget as HTMLButtonElement).style.color = "#888"; }}
                      >← Prev</button>
                      <div className="flex gap-2">
                        {ANNOUNCE_STEPS.map((_, i) => (
                          <button key={i} onClick={() => setActiveAnnounceStep(i)}
                            className="rounded-full transition-all duration-300"
                            style={{ width: activeAnnounceStep === i ? 20 : 8, height: 8, background: activeAnnounceStep === i ? GOLD : "rgba(212,160,23,0.3)" }}
                          />
                        ))}
                      </div>
                      <button onClick={next}
                        className="flex-1 flex items-center justify-center py-3 rounded-xl text-xs uppercase tracking-widest font-semibold transition-all duration-200"
                        style={{ background: "rgba(255,255,255,0.05)", color: "#888", border: "1px solid rgba(212,160,23,0.2)" }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.6)"; (e.currentTarget as HTMLButtonElement).style.color = GOLD; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = "rgba(212,160,23,0.2)"; (e.currentTarget as HTMLButtonElement).style.color = "#888"; }}
                      >Next →</button>
                    </div>
                  </div>
                </motion.div>
              );
            })()}

            {/* Lightbox */}
            <AnimatePresence>
              {announceZoom && (
                <motion.div
                  key="announce-zoom"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-zoom-out"
                  style={{ background: "rgba(0,0,0,0.92)" }}
                  onClick={() => setAnnounceZoom(false)}
                >
                  <motion.div
                    initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ maxWidth: "90vw", maxHeight: "88vh", border: "1px solid rgba(212,160,23,0.4)" }}
                    onClick={e => e.stopPropagation()}
                  >
                    {(() => {
                      const imgs = ["/announce-step1.webp", "/announce-step2.webp", "/announce-step3.webp", "/announce-step4.webp"];
                      return <img src={imgs[activeAnnounceStep]} alt={`Step ${activeAnnounceStep + 1}`}
                        style={{ maxWidth: "85vw", maxHeight: "80vh", objectFit: "contain", display: "block" }} />;
                    })()}
                    <button onClick={() => setAnnounceZoom(false)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: "rgba(0,0,0,0.7)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)" }}>✕</button>
                    <div className="absolute bottom-0 left-0 right-0 px-5 py-3 text-center" style={{ background: "rgba(0,0,0,0.7)" }}>
                      <span className="text-xs uppercase tracking-widest" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>
                        Step {activeAnnounceStep + 1} of 4
                      </span>
                    </div>
                  </motion.div>
                  <p className="absolute bottom-6 text-xs uppercase tracking-widest text-gray-500">Click anywhere to close</p>
                </motion.div>
              )}
            </AnimatePresence>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 5 — DAY IN THE LIFE
      ════════════════════════════════════ */}
      {/* @section: daylife */}
      <section id="daylife" className="relative py-24 px-6" style={{ background: "linear-gradient(180deg, #000 0%, #080500 50%, #000 100%)" }}>
        <div className="max-w-6xl mx-auto">
          <AnimSection>
            <div className="text-center mb-16">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                Chapter 04
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(44px, 7vw, 78px)", letterSpacing: 3, lineHeight: 1 }}
              >
                DAY IN THE LIFE<br />
                <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  OF A PROFESSIONAL FIGHTER
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto mt-4"><GoldDivider /></motion.div>
              <motion.p variants={fadeUp} className="text-gray-400 text-sm uppercase tracking-widest mt-6 max-w-2xl mx-auto leading-relaxed">
                Show us your day to day. Capture{" "}
                <span style={{ color: GOLD, fontWeight: 600 }}>5–6 videos at 30 seconds each</span>.
                The signing moment lives inside a larger narrative about who you are,
                how you train, and what you do outside of the ring.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3 justify-center mt-6">
                {["IG Live", "TikTok", "Short-Form Haul Video"].map((p) => (<PlatformTag key={p} label={p} />))}
              </motion.div>
            </div>

            {/* Three pillars */}
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {[
                {
                  icon: "🥊", title: "Training", subtitle: "In the Gym", img: "/daylife1.webp", video: "/daylife-training.mp4",
                  steps: [
                    "Film yourself working out — shadowboxing, bag work, sparring",
                    "Wear OH YAAS × BKFC gear in the session",
                    "Show your intensity and dedication to the craft",
                  ],
                },
                {
                  icon: "✍️", title: "Signing", subtitle: "The Ritual", img: "/daylife2.webp",
                  steps: [
                    "Document the signing kit opening moment on camera",
                    "Execute the ritual — hat on, chest slap, air signature, etc.",
                    "Make it feel like an arrival, not just an unboxing",
                  ],
                },
                {
                  icon: "🎙️", title: "Identity", subtitle: "Your Story", img: "/daylife3.webp", video: "/daylife-identity.mp4",
                  steps: [
                    "Talk to camera about what signing with BKFC and OH YAAS means to you",
                    "Scripted or unscripted — just be authentic",
                    "Let your personality and story come through",
                  ],
                },
              ].map((pillar: { icon: string; title: string; subtitle: string; img: string; video?: string; steps: string[] }, pi) => (
                <motion.div
                  key={pi}
                  variants={fadeUp}
                  custom={pi * 0.15}
                  className="rounded-2xl overflow-hidden flex flex-col"
                  style={{ border: "1px solid rgba(212,160,23,0.2)", background: "rgba(255,255,255,0.02)" }}
                  whileHover={{ borderColor: "rgba(212,160,23,0.5)", y: -4, transition: { duration: 0.3 } }}
                >
                  <div
                    className="relative cursor-zoom-in"
                    style={{ aspectRatio: "16/9" }}
                    onClick={() => setDayZoom(pi)}
                  >
                    {pillar.video ? (
                      <video
                        src={pillar.video}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={pillar.img} alt={pillar.title} className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 60%)" }} />
                    <div className="absolute top-2 right-2 px-2 py-1 rounded-full text-xs uppercase tracking-widest"
                      style={{ background: "rgba(0,0,0,0.6)", color: GOLD, border: "1px solid rgba(212,160,23,0.3)" }}>
                      ⤢ Expand
                    </div>
                    <div className="absolute bottom-4 left-4 flex items-baseline gap-2">
                      <span className="text-2xl">{pillar.icon}</span>
                      <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, letterSpacing: 2, color: GOLD }}>
                        {pillar.title}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <p className="text-xs uppercase tracking-widest mb-4 text-gray-500">{pillar.subtitle}</p>
                    <div className="flex flex-col gap-3 flex-1">
                      {pillar.steps.map((step, si) => (
                        <div key={si} className="flex gap-3 items-start">
                          <span
                            className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                            style={{ border: `1px solid ${GOLD}`, color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}
                          >
                            {si + 1}
                          </span>
                          <p className="text-gray-400 text-xs leading-relaxed uppercase tracking-wide">{step}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Summary banner */}
            <motion.div
              variants={fadeUp}
              className="rounded-2xl p-8 text-center"
              style={{ background: "linear-gradient(135deg, rgba(212,160,23,0.12), rgba(212,160,23,0.04))", border: "1px solid rgba(212,160,23,0.3)" }}
            >
              <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(28px, 4vw, 48px)", letterSpacing: 3 }}>
                CAPTURE YOUR STORY IN{" "}
                <span style={{ color: GOLD }}>5–6 VIDEOS</span>
              </p>
              <p className="text-gray-400 text-sm uppercase tracking-widest mt-3">
                30 seconds each · Training · Signing · Identity
              </p>
            </motion.div>

            {/* Day in the Life Lightbox */}
            <AnimatePresence>
              {dayZoom >= 0 && (
                <motion.div
                  key="day-zoom"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4 cursor-zoom-out"
                  style={{ background: "rgba(0,0,0,0.92)" }}
                  onClick={() => setDayZoom(-1)}
                >
                  <motion.div
                    initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.85, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-2xl overflow-hidden"
                    style={{ maxWidth: "90vw", maxHeight: "88vh", border: "1px solid rgba(212,160,23,0.4)" }}
                    onClick={e => e.stopPropagation()}
                  >
                    {(dayZoom === 0 || dayZoom === 2) ? (
                      <video
                        key={dayZoom}
                        src={dayZoom === 0 ? "/daylife-training.mp4" : "/daylife-identity.mp4"}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls
                        style={{ maxWidth: "85vw", maxHeight: "80vh", display: "block" }}
                      />
                    ) : (
                      <img
                        src={["/daylife1.webp", "/daylife2.webp", "/daylife3.webp"][dayZoom]}
                        alt={["Training", "Signing", "Identity"][dayZoom]}
                        style={{ maxWidth: "85vw", maxHeight: "80vh", objectFit: "contain", display: "block" }}
                      />
                    )}
                    <button onClick={() => setDayZoom(-1)}
                      className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: "rgba(0,0,0,0.7)", color: "#D4A017", border: "1px solid rgba(212,160,23,0.4)" }}>✕</button>
                    <div className="absolute bottom-0 left-0 right-0 px-5 py-3 text-center" style={{ background: "rgba(0,0,0,0.7)" }}>
                      <span className="text-xs uppercase tracking-widest" style={{ color: "#D4A017", fontFamily: "'JetBrains Mono', monospace" }}>
                        {["🥊 Training", "✍️ Signing", "🎙️ Identity"][dayZoom]}
                      </span>
                    </div>
                  </motion.div>
                  <p className="absolute bottom-6 text-xs uppercase tracking-widest text-gray-500">Click anywhere to close</p>
                </motion.div>
              )}
            </AnimatePresence>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 6 — SHIPPING INSTRUCTIONS
      ════════════════════════════════════ */}
      {/* @section: shipping */}
      <section id="shipping" className="relative py-24 px-6" style={{ background: "linear-gradient(180deg, #000 0%, #060400 50%, #000 100%)" }}>
        <div className="max-w-6xl mx-auto">
          <AnimSection>
            {/* Header */}
            <div className="text-center mb-14">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                Chapter 05
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", letterSpacing: 3, lineHeight: 1 }}
              >
                SHIPPING{" "}
                <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  INSTRUCTIONS
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto mt-4"><GoldDivider /></motion.div>
              <motion.p variants={fadeUp} className="text-gray-400 text-sm uppercase tracking-widest mt-4">
                Welcome to the OH YAAS Team — Here's What to Do
              </motion.p>
            </div>

            {/* Overview 4-step banner */}
            <motion.div variants={fadeUp} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
              {[
                { num: "01", video: "/ship-step1.mp4", label: "Sign Stickers",    sub: "Record 2 mins of video" },
                { num: "02", video: "/ship-step2.mp4", label: "Sign Documents",   sub: "Attestation form" },
                { num: "03", video: "/ship-step3.mp4", label: "Pack Relics",      sub: "In the clear bag" },
                { num: "04", video: "/ship-step4.mp4", label: "Return Everything",sub: "Stickers, relics & docs in mailer" },
              ].map((s) => (
                <div
                  key={s.num}
                  className="flex flex-col rounded-xl overflow-hidden"
                  style={{ background: "rgba(212,160,23,0.07)", border: "1px solid rgba(212,160,23,0.25)" }}
                >
                  {/* Autoplay video */}
                  <div className="relative" style={{ aspectRatio: "9/12" }}>
                    <video
                      src={s.video}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 55%)" }} />
                    <span
                      className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{ background: "rgba(0,0,0,0.7)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)", fontFamily: "'JetBrains Mono', monospace" }}
                    >{s.num}</span>
                  </div>
                  {/* Text */}
                  <div className="p-4 text-center">
                    <p className="text-white font-semibold text-sm uppercase tracking-wide mb-1">{s.label}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{s.sub}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Tab bar */}
            <motion.div variants={fadeUp} className="flex gap-2 mb-10 overflow-x-auto pb-1">
              {["✍️ Sticker Signing", "📬 Autograph Shipping", "🥊 Relic Shipping"].map((label, i) => (
                <button
                  key={i}
                  onClick={() => setShippingTab(i)}
                  className="flex-shrink-0 px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold transition-all duration-300"
                  style={
                    shippingTab === i
                      ? { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, color: "#000" }
                      : { background: "rgba(255,255,255,0.05)", color: "#888", border: "1px solid rgba(212,160,23,0.2)" }
                  }
                >
                  {label}
                </button>
              ))}
            </motion.div>

            {/* Tab panels */}
            <AnimatePresence mode="wait">

              {/* ── TAB 0: Sticker Signing ── */}
              {shippingTab === 0 && (
                <motion.div
                  key="tab-sign"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="grid md:grid-cols-2 gap-12 items-start">
                    {/* Steps */}
                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Signing Rules</p>
                      {[
                        "Sign all stickers on a flat surface",
                        "Record a 2-minute video of you signing the entire session",
                        "Let each sheet dry for 60 seconds before stacking",
                        "Only use the pens provided in your kit",
                        "Stay fully inside the sticker — no overflow",
                        "Keep your autograph consistent across all sheets",
                        "Do not add extra elements other than your signature",
                      ].map((t, i) => (
                        <div key={i} className="flex gap-4 items-start p-4 rounded-xl"
                          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,160,23,0.15)" }}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ border: `2px solid ${GOLD}`, color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</div>
                          <p className="text-gray-300 text-sm leading-relaxed uppercase tracking-wide mt-1">{t}</p>
                        </div>
                      ))}

                      {/* After signing callout */}
                      <div className="mt-4 p-5 rounded-xl" style={{ background: "rgba(212,160,23,0.1)", border: `1px solid rgba(212,160,23,0.4)` }}>
                        <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD }}>✅ After Signing All Stickers</p>
                        {[
                          "Upload the 2-minute signing video",
                          "Sign the attestation document",
                          "Ship back autographs from your local UPS Store",
                        ].map((t, i) => (
                          <div key={i} className="flex gap-3 items-start mb-2">
                            <span className="text-xs mt-1" style={{ color: GOLD }}>→</span>
                            <p className="text-gray-300 text-xs uppercase tracking-wide leading-relaxed">{t}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Sequential Video Player */}
                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Reference Videos</p>
                      {(() => {
                        const SIGN_VIDS = [
                          { src: "/ship-signing.mp4", label: "Signing Demo" },
                          { src: "/ship-sign2.mp4",   label: "Sign Stickers Overview" },
                        ];
                        return (
                          <div className="flex flex-col gap-3">
                            <div className="relative rounded-2xl overflow-hidden"
                              style={{ border: "1px solid rgba(212,160,23,0.35)", background: "#000" }}>
                              <video
                                key={shipSignVidIdx}
                                src={SIGN_VIDS[shipSignVidIdx].src}
                                autoPlay
                                playsInline
                                controls
                                className="w-full block"
                                style={{ maxHeight: 480, background: "#000" }}
                                onEnded={() => setShipSignVidIdx((shipSignVidIdx + 1) % SIGN_VIDS.length)}
                              />
                              <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-semibold"
                                style={{ background: "rgba(0,0,0,0.75)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)", fontFamily: "'JetBrains Mono', monospace" }}>
                                ▶ {SIGN_VIDS[shipSignVidIdx].label}
                              </div>
                              <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs"
                                style={{ background: "rgba(0,0,0,0.7)", color: "#888", fontFamily: "'JetBrains Mono', monospace" }}>
                                {shipSignVidIdx + 1} / {SIGN_VIDS.length}
                              </div>
                            </div>
                            {/* Pill-dot indicators */}
                            <div className="flex justify-center gap-2">
                              {SIGN_VIDS.map((v, i) => (
                                <button
                                  key={i}
                                  onClick={() => setShipSignVidIdx(i)}
                                  className="h-2 rounded-full transition-all duration-300"
                                  style={{
                                    width: shipSignVidIdx === i ? 28 : 8,
                                    background: shipSignVidIdx === i ? GOLD : "rgba(212,160,23,0.3)",
                                  }}
                                />
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 uppercase tracking-widest text-center">
                              Watch how to sign stickers correctly — follow this technique
                            </p>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── TAB 1: Autograph Shipping ── */}
              {shippingTab === 1 && (
                <motion.div
                  key="tab-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="grid md:grid-cols-2 gap-12 items-start">
                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Packaging Steps</p>
                      {[
                        "Place all signed sticker sheets into the yellow envelope",
                        "Place the yellow envelope into the larger white envelope",
                        "Place the white envelope into the UPS shipping envelope",
                        "Place the signed attestation document into the UPS shipping envelope",
                        "Seal the UPS envelope completely",
                        "Take it to your local UPS Store, drop off and get a confirmation receipt",
                      ].map((t, i) => (
                        <div key={i} className="flex gap-4 items-start p-4 rounded-xl"
                          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,160,23,0.15)" }}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ border: `2px solid ${GOLD}`, color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</div>
                          <p className="text-gray-300 text-sm leading-relaxed uppercase tracking-wide mt-1">{t}</p>
                        </div>
                      ))}
                      <div className="mt-2 p-5 rounded-xl" style={{ background: "rgba(212,160,23,0.07)", border: "1px solid rgba(212,160,23,0.3)" }}>
                        <p className="text-sm font-semibold uppercase tracking-widest mb-1" style={{ color: GOLD }}>📦 Envelope Order</p>
                        <p className="text-gray-400 text-xs uppercase tracking-wide leading-relaxed">
                          Sticker sheets → Yellow envelope → White envelope → UPS Express envelope + Attestation doc
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Reference Video</p>
                      <div className="relative rounded-2xl overflow-hidden"
                        style={{ border: "1px solid rgba(212,160,23,0.35)", background: "#000" }}>
                        <video
                          src="/ship-signing.mp4"
                          controls
                          playsInline
                          className="w-full block"
                          style={{ maxHeight: 480, background: "#000" }}
                        />
                        <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs uppercase tracking-widest font-semibold"
                          style={{ background: "rgba(0,0,0,0.75)", color: GOLD, border: "1px solid rgba(212,160,23,0.4)", fontFamily: "'JetBrains Mono', monospace" }}>
                          ▶ Signing Demo
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 uppercase tracking-widest text-center">
                        Reference this video when preparing your autograph package
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ── TAB 2: Relic Shipping ── */}
              {shippingTab === 2 && (
                <motion.div
                  key="tab-relic"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* AUTOS FIRST warning */}
                  <div className="mb-8 p-5 rounded-xl flex gap-4 items-center"
                    style={{ background: "rgba(212,160,23,0.12)", border: `2px solid rgba(212,160,23,0.5)` }}>
                    <span className="text-3xl">⚠️</span>
                    <div>
                      <p className="text-sm font-bold uppercase tracking-widest" style={{ color: GOLD }}>Autos First!</p>
                      <p className="text-gray-300 text-xs uppercase tracking-wide mt-1">
                        Relics can only be returned AFTER autographs have been shipped. Complete the sticker signing steps first.
                      </p>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-12 items-start">
                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Relic Packaging Steps</p>
                      {[
                        "Fill out the relic label — write your name and item description",
                        "Check the box: Practice, Sparring, or Match Worn",
                        "Fold the item and label it with its origin",
                        "Place item(s) in the clear bag and seal it",
                        "Place the sealed clear bag in the padded UPS bag with the shipping return label",
                        "Take to UPS Store and ship back",
                      ].map((t, i) => (
                        <div key={i} className="flex gap-4 items-start p-4 rounded-xl"
                          style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,160,23,0.15)" }}>
                          <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                            style={{ border: `2px solid ${GOLD}`, color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>{i + 1}</div>
                          <p className="text-gray-300 text-sm leading-relaxed uppercase tracking-wide mt-1">{t}</p>
                        </div>
                      ))}

                      {/* Label details */}
                      <div className="mt-2 p-5 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,160,23,0.2)" }}>
                        <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: GOLD }}>🏷️ On the Label, Write:</p>
                        {["Your Name", "Item Description (e.g. shorts, gloves)", "Check one: Practice · Sparring · Match Worn"].map((t, i) => (
                          <div key={i} className="flex gap-3 items-start mb-2">
                            <span style={{ color: GOLD }} className="text-xs mt-1">→</span>
                            <p className="text-gray-300 text-xs uppercase tracking-wide leading-relaxed">{t}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col gap-4">
                      <p className="text-xs uppercase tracking-widest mb-2" style={{ color: GOLD }}>Reference Photos</p>
                      <div className="grid grid-cols-2 gap-3">
                        {[IMG.shipRelic1, IMG.shipRelicLabel, IMG.shipRelicBag, IMG.shipRelicPadded].map((src, i) => (
                          <div key={i} className="relative overflow-hidden rounded-xl"
                            style={{ aspectRatio: "4/3", border: "1px solid rgba(212,160,23,0.15)" }}>
                            <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        {[IMG.shipRelicUPS, IMG.shipRelicPack1, IMG.shipRelicPack2].map((src, i) => (
                          <div key={i} className="relative overflow-hidden rounded-xl"
                            style={{ aspectRatio: "1/1", border: "1px solid rgba(212,160,23,0.15)" }}>
                            <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Kit contents footer */}
            <motion.div variants={fadeUp} className="mt-14 p-6 rounded-2xl"
              style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(212,160,23,0.2)" }}>
              <p className="text-xs uppercase tracking-widest mb-4" style={{ color: GOLD }}>📦 What's in Your Kit</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  "Manila envelope",
                  "Return label for mailers",
                  "Ziplock bag labels",
                  "Photo at start & finish of signing",
                  "2-minute signing video",
                  "Upload via QR link",
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                    <p className="text-gray-400 text-xs uppercase tracking-wide">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </AnimSection>
        </div>
      </section>

      {/* ════════════════════════════════════
          SECTION 7 — THANK YOU
      ════════════════════════════════════ */}
      {/* @section: collecting */}
      <section id="collecting" className="relative py-24 px-6" style={{ background: "linear-gradient(180deg, #000 0%, #060400 50%, #000 100%)" }}>
        <div className="max-w-5xl mx-auto">
          <AnimSection>

            {/* Header */}
            <div className="text-center mb-14">
              <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: GOLD }}>
                OH YAAS Podcast
              </motion.p>
              <motion.h2
                variants={fadeUp}
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(48px, 7vw, 80px)", letterSpacing: 3, lineHeight: 1 }}
              >
                COLLECTING{" "}
                <span style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  MATTERS
                </span>
              </motion.h2>
              <motion.div variants={fadeUp} className="max-w-xs mx-auto mt-4"><GoldDivider /></motion.div>
            </div>

            {/* YouTube video — primary visual */}
            <motion.div
              variants={fadeUp}
              className="rounded-2xl overflow-hidden mb-12"
              style={{ border: "1px solid rgba(212,160,23,0.35)", aspectRatio: "16/9", position: "relative" }}
            >
              <iframe
                id="yt-collecting"
                src="https://www.youtube.com/embed/1owc3Fk3S1c?autoplay=0&rel=0&enablejsapi=1"
                title="Collecting Matters — Kai Stewart Episode"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                style={{ border: "none" }}
              />
            </motion.div>

            {/* Description */}
            <div className="grid md:grid-cols-2 gap-10 mb-14">
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: GOLD }}>About the Podcast</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Collecting Matters is a podcast dedicated to the world of collectibles, bringing together athletes, entertainers, artists, and collectors to share stories, passions, and insights from the hobby.
                </p>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-col gap-4">
                <p className="text-xs uppercase tracking-widest mb-1" style={{ color: GOLD }}>Featured Episode</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  We're excited to launch our first episode featuring BKFC star <span style={{ color: GOLD, fontWeight: 600 }}>Kai Stewart</span>, who joins us to discuss his career, fan engagement, and the growing connection between combat sports and collectibles.
                </p>
              </motion.div>
            </div>

            {/* Guest Interest Form */}
            <motion.div
              variants={fadeUp}
              className="rounded-2xl p-8 md:p-10"
              style={{ background: "linear-gradient(135deg, rgba(212,160,23,0.08), rgba(212,160,23,0.03))", border: "1px solid rgba(212,160,23,0.3)" }}
            >
              <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>Be a Guest</p>
              <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(22px, 3vw, 36px)", letterSpacing: 2, lineHeight: 1.1 }} className="mb-3">
                ARE YOU ALL BKFC FIGHTERS!
              </p>
              <p className="text-gray-400 text-sm leading-relaxed mb-8 max-w-xl">
                Are you a BKFC fighter interested in being a guest on Collecting Matters? We'd love to hear your story. To be considered for a future episode, enter your email below.
              </p>

              <div className="flex flex-col gap-4">
                {/* Email */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>Your Email</label>
                  <input
                    type="email"
                    value={collectEmail}
                    onChange={e => setCollectEmail(e.target.value)}
                    placeholder="fighter@example.com"
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 outline-none transition-all duration-200"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(212,160,23,0.25)", fontFamily: "'Inter', sans-serif" }}
                    onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
                    onBlur={e => (e.currentTarget.style.borderColor = "rgba(212,160,23,0.25)")}
                  />
                </div>

                {/* Comments */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs uppercase tracking-widest" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>Comments</label>
                  <textarea
                    value={collectComment}
                    onChange={e => setCollectComment(e.target.value)}
                    placeholder="Tell us about yourself, your fighting career, and why you'd be a great guest..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 outline-none transition-all duration-200 resize-none"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(212,160,23,0.25)", fontFamily: "'Inter', sans-serif" }}
                    onFocus={e => (e.currentTarget.style.borderColor = GOLD)}
                    onBlur={e => (e.currentTarget.style.borderColor = "rgba(212,160,23,0.25)")}
                  />
                </div>

                {/* Submit */}
                <div className="flex justify-end">
                  <a
                    href={`mailto:ra@youareallstars.com?subject=Collecting%20Matters%20Guest%20Interest&body=Email%3A%20${encodeURIComponent(collectEmail)}%0A%0AComments%3A%0A${encodeURIComponent(collectComment)}`}
                    className="inline-flex items-center gap-3 px-8 py-3 rounded-full text-sm font-semibold uppercase tracking-widest transition-all duration-300"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, color: "#000" }}
                    onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
                    onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
                  >
                    <span>✉</span>
                    Submit
                  </a>
                </div>
              </div>
            </motion.div>

          </AnimSection>
        </div>
      </section>

      {/* @section: thanks */}
      <section
        id="thanks"
        className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-24"
      >
        <div className="absolute inset-0 z-0" style={{ background: "radial-gradient(ellipse at center, rgba(212,160,23,0.07) 0%, transparent 70%)" }} />

        <AnimSection className="relative z-10 max-w-3xl mx-auto w-full">
          <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.4em] mb-6" style={{ color: GOLD }}>
            You're Ready
          </motion.p>

          <motion.h2
            variants={fadeUp}
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(72px, 16vw, 150px)",
              lineHeight: 0.9,
              letterSpacing: 6,
              background: `linear-gradient(135deg, #fff 0%, ${GOLD} 60%, ${GOLD_LIGHT} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            THANK<br />YOU!
          </motion.h2>

          <motion.div variants={fadeUp} className="my-8 max-w-sm mx-auto"><GoldDivider /></motion.div>

          <motion.p variants={fadeUp} className="text-gray-400 text-sm uppercase tracking-widest leading-relaxed mb-12">
            Send us your videos. Let's build something iconic together.<br />
            You're now part of the OH YAAS family.
          </motion.p>

          {/* Recap cards */}
          <div className="grid sm:grid-cols-2 gap-4 text-left mb-12">
            {[
              { num: "01", title: "The Signing Moment", desc: "Put on gear · Engage with logo · Signal your arrival" },
              { num: "02", title: "Haul & Livestream", desc: "Unbox live · Scan QR code · React to your card" },
              { num: "03", title: "Announcement Series", desc: "Show the card · Flip it · Explain why you're excited" },
              { num: "04", title: "Day in the Life", desc: "5–6 videos · Training · Signing · Identity" },
            ].map((c) => (
              <motion.div
                key={c.num}
                variants={fadeUp}
                className="p-5 rounded-xl"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(212,160,23,0.15)" }}
                whileHover={{ borderColor: "rgba(212,160,23,0.5)", transition: { duration: 0.2 } }}
              >
                <p className="text-xs mb-1" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>{c.num}</p>
                <p className="text-white font-semibold text-sm uppercase tracking-wide mb-1">{c.title}</p>
                <p className="text-gray-500 text-xs leading-relaxed">{c.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} className="flex justify-center">
            <img src="/ohyaas.svg" alt="OH YAAS!" style={{ height: 64, opacity: 0.45 }} />
          </motion.div>

          {/* Ra Kazadi Contact Card */}
          <motion.div
            variants={fadeUp}
            className="mt-14 rounded-2xl overflow-hidden text-left"
            style={{ border: "1px solid rgba(212,160,23,0.35)", background: "rgba(255,255,255,0.02)" }}
          >
            <div className="grid md:grid-cols-2 gap-0">
              {/* Video */}
              <div className="relative" style={{ minHeight: 280 }}>
                <video
                  ref={raVideoRef}
                  src="/ra-kazadi-v2.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  style={{ minHeight: 280 }}
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to right, transparent 70%, rgba(0,0,0,0.6) 100%)" }} />
                {/* Mute / Unmute toggle */}
                <button
                  onClick={() => {
                    if (raVideoRef.current) {
                      raVideoRef.current.muted = !raVideoRef.current.muted;
                      setRaMuted(raVideoRef.current.muted);
                      // Pause bg music when unmuting Ra's video
                      if (!raVideoRef.current.muted && bgAudioRef.current) {
                        bgAudioRef.current.pause();
                        setBgMuted(true);
                      }
                    }
                  }}
                  className="absolute bottom-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs uppercase tracking-widest font-semibold transition-all duration-200"
                  style={{ background: "rgba(0,0,0,0.75)", color: raMuted ? "#888" : GOLD, border: `1px solid ${raMuted ? "rgba(255,255,255,0.15)" : "rgba(212,160,23,0.5)"}`, fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {raMuted ? "🔇 Unmute" : "🔊 Mute"}
                </button>
              </div>

              {/* Text */}
              <div className="flex flex-col justify-center p-8 gap-5">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] mb-1" style={{ color: GOLD, fontFamily: "'JetBrains Mono', monospace" }}>
                    A Message From OH YAAS
                  </p>
                  <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(22px, 3vw, 32px)", letterSpacing: 2, lineHeight: 1.1 }}>
                    RA KAZADI
                  </p>
                  <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">Director of Marketing · OH YAAS</p>
                </div>

                <p className="text-gray-300 text-sm leading-relaxed" style={{ fontStyle: "italic" }}>
                  "Hey everyone, I'm Ra Kazadi, Director of Marketing for Oh YAAS. We're excited to partner with BKFC and help you connect with fans, build your brand, and unlock new opportunities. If you need anything, feel free to reach out to me directly."
                </p>

                <a
                  href="mailto:ra@youareallstars.com?subject=BKFC%20Fighter%20Question"
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-full text-sm font-semibold uppercase tracking-widest transition-all duration-300 self-start"
                  style={{
                    background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                    color: "#000",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
                >
                  <span>✉</span>
                  ra@youareallstars.com
                </a>
              </div>
            </div>
          </motion.div>

        </AnimSection>

        <motion.button
          className="absolute bottom-8 right-8 w-12 h-12 rounded-full flex items-center justify-center text-lg"
          style={{ border: `1px solid rgba(212,160,23,0.4)`, color: GOLD, background: "rgba(212,160,23,0.05)" }}
          onClick={() => scrollTo("hero")}
          whileHover={{ scale: 1.1, background: "rgba(212,160,23,0.18)", transition: { duration: 0.2 } }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          aria-label="Back to top"
        >
          ↑
        </motion.button>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="py-6 px-6 text-center"
        style={{ borderTop: "1px solid rgba(212,160,23,0.12)" }}
      >
        <p className="text-xs uppercase tracking-widest text-gray-600">
          ©2026 Oh YAAS™ — Experiential Collectibles™ Partner Program
        </p>
      </footer>
    </div>
  );
}
